import { gql } from "@apollo/client";
import { ReactElement, useState } from "react";
import { Button } from "@src/lib/ui/Button";
import Text from "@src/lib/ui/Text";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@src/lib/ui/Dialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@src/lib/ui/AlertDialog";
import { RadioGroup, RadioGroupItem } from "@src/lib/ui/RadioGroup";
import { Label } from "@src/lib/ui/Label";
import { useWipeTrialOrgMutation } from "./WipeTrialOrgDialog.generated";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: number;
  orgName: string;
};

export default function WipeTrialOrgDialog({
  open,
  onOpenChange,
  orgId,
  orgName,
}: Props): ReactElement {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [queued, setQueued] = useState(false);
  const [submitError, setSubmitError] = useState<string>();
  const [keepOrgSetup, setKeepOrgSetup] = useState(true);
  const [keepNonArchivedContent, setKeepNonArchivedContent] = useState(true);
  const [wipeTrialOrg, { loading: submitting }] = useWipeTrialOrgMutation();

  const submit = async (): Promise<void> => {
    setSubmitError(undefined);
    try {
      const result = await wipeTrialOrg({
        variables: {
          input: { expectedOrgId: orgId, keepOrgSetup, keepNonArchivedContent },
        },
      });
      if (!result.data?.wipeTrialOrg.success) {
        throw new Error(
          result.data?.wipeTrialOrg.error?.message ||
            "The wipe could not be started.",
        );
      }
      setQueued(true);
      setConfirmOpen(false);
    } catch (e) {
      setSubmitError(
        e instanceof Error ? e.message : "The wipe could not be started.",
      );
    }
  };

  return (
    <Dialog
      open={open && !confirmOpen}
      onOpenChange={(nextOpen) => {
        if (!submitting && !confirmOpen) onOpenChange(nextOpen);
      }}
    >
      {/* Only one focus trap is mounted while moving between these steps. */}
      {!confirmOpen && (
        <DialogContent
          hideDefaultTitle
          className="flex max-h-[80vh] max-w-xl flex-col overflow-hidden"
        >
          <DialogHeader>
            <DialogTitle>Wipe trial org</DialogTitle>
            <DialogDescription>
              {orgName} · Org ID {orgId}
            </DialogDescription>
          </DialogHeader>
          <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto">
            {queued ? (
              <Text variant="compact-description" multiline role="status">
                Trial wipe queued with your choices. This may take a few minutes
                to finish in the background. Close this dialog and refresh the
                page later to see the result.
              </Text>
            ) : (
              <>
                <WipeChoice
                  id="wipe-setup"
                  label="Locations, roles, regions, departments, tags & folders"
                  keepLabel="Keep existing"
                  keep={keepOrgSetup}
                  onChange={setKeepOrgSetup}
                  disabled={submitting}
                />
                <div className="flex flex-col gap-2">
                  <WipeChoice
                    id="wipe-content"
                    label="Content"
                    keepLabel="Keep non-archived"
                    keep={keepNonArchivedContent}
                    onChange={setKeepNonArchivedContent}
                    disabled={submitting}
                  />
                  <Text
                    variant="compact-description"
                    multiline
                    className="text-text-secondary"
                  >
                    All archived content is automatically hard deleted.
                  </Text>
                </div>
                <Text
                  variant="compact-description"
                  multiline
                  className="text-text-secondary"
                >
                  Always removes automations, assignment sources, posts, chats,
                  HRIS connections, skill schedule windows, and demo users and
                  their progress and compliance records. This also affects
                  customer-created data in these categories.
                </Text>
              </>
            )}
          </div>
          <DialogFooter className="shrink-0">
            <Button
              variant={queued ? "default" : "outline"}
              disabled={submitting}
              onClick={() => onOpenChange(false)}
            >
              {queued ? "Close" : "Cancel"}
            </Button>
            {!queued && (
              <Button
                variant="destructive"
                disabled={submitting}
                onClick={() => setConfirmOpen(true)}
              >
                Review wipe
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      )}
      {confirmOpen && (
        <AlertDialog
          open={confirmOpen}
          onOpenChange={submitting ? undefined : setConfirmOpen}
        >
          <AlertDialogContent className="max-h-[80vh] overflow-y-auto">
            <AlertDialogHeader>
              <AlertDialogTitle>
                Wipe trial data for {orgName}?
              </AlertDialogTitle>
              <AlertDialogDescription>
                Permanently delete archived content, automations and assignment
                sources, posts, chats, HRIS connections, skill schedule windows,
                demo users and their records. Your choices below determine what
                else is deleted.{" "}
                <strong className="font-bold text-text-primary">
                  This cannot be undone.
                </strong>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="flex flex-col gap-2">
              <Text variant="compact-description" multiline>
                <strong>
                  Locations, roles, regions, departments, tags & folders:
                </strong>{" "}
                {keepOrgSetup ? "Keep existing" : "Delete all"}
              </Text>
              <Text variant="compact-description" multiline>
                <strong>Content:</strong>{" "}
                {keepNonArchivedContent ? "Keep non-archived" : "Delete all"}
              </Text>
              {submitError && (
                <Text
                  variant="compact-description"
                  multiline
                  role="alert"
                  className="text-status-error-text"
                >
                  {submitError}
                </Text>
              )}
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel asChild>
                <Button variant="outline" disabled={submitting}>
                  Cancel
                </Button>
              </AlertDialogCancel>
              <Button
                variant="destructive"
                loading={submitting}
                disabled={submitting}
                onClick={submit}
              >
                Wipe trial org
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </Dialog>
  );
}

function WipeChoice({
  id,
  label,
  keepLabel,
  keep,
  onChange,
  disabled,
}: {
  id: string;
  label: string;
  keepLabel: string;
  keep: boolean;
  onChange: (keep: boolean) => void;
  disabled: boolean;
}): ReactElement {
  return (
    <div className="flex flex-col gap-2">
      <Text id={`${id}-label`} variant="compact-title" multiline>
        {label}
      </Text>
      <RadioGroup
        aria-labelledby={`${id}-label`}
        value={keep ? "keep" : "delete"}
        onValueChange={(value) => onChange(value === "keep")}
        disabled={disabled}
        className="grid-cols-2"
      >
        <div className="flex items-center gap-2">
          <RadioGroupItem id={`${id}-keep`} value="keep" />
          <Label htmlFor={`${id}-keep`}>{keepLabel}</Label>
        </div>
        <div className="flex items-center gap-2">
          <RadioGroupItem id={`${id}-delete`} value="delete" />
          <Label htmlFor={`${id}-delete`}>Delete all</Label>
        </div>
      </RadioGroup>
    </div>
  );
}

gql`
  mutation WipeTrialOrg($input: WipeTrialOrgInput!) {
    wipeTrialOrg(input: $input) {
      success
      error {
        message
      }
    }
  }
`;
